import { AxiosResponse } from 'axios';
import { MutationFunction, useMutation, UseMutationResult } from 'react-query';
import {
    getDefaultHeaders,
    instance as axiosInstance,
    useAlert,
    withHeaders,
} from '../core';
import { commonShowAlert } from '../lib';
import { FileData } from '../pages/job-manager/components/create-job-dialog';

interface PostPredictDictionaryProps {
    file: FileData;
    token: string;
    sheetName?: string;
    sheetNum?: string;
    description?: string;
    primaryIndexCols?: string[];
    maxRowsToUse?: string;
}

export const postPredictDictionary: MutationFunction<
    AxiosResponse<any>,
    PostPredictDictionaryProps
> = async (props: PostPredictDictionaryProps) => {
    const {
        file,
        token,
        sheetName,
        sheetNum,
        primaryIndexCols,
        maxRowsToUse,
        description,
    } = props;
    const headers = getDefaultHeaders(token);
    const url = '/metadata/dictionary/predict';

    const formData = new FormData();
    const response = await fetch(file.url);
    const blob = await response.blob();
    formData.append('file', blob, file.name);
    if (sheetName) {
        formData.append('sheetName', sheetName);
    }
    if (sheetNum) {
        formData.append('sheetNum', sheetNum);
    }
    if (maxRowsToUse) {
        formData.append('maxRowsToUse', maxRowsToUse.toString());
    }
    if (description) {
        formData.append('description', description);
    }
    if (primaryIndexCols?.length) {
        for (const column of primaryIndexCols) {
            formData.append('primaryIndexCols', column);
        }
    }
    return axiosInstance().post(url, formData, withHeaders(headers));
};

export const usePredictDictionary = (): UseMutationResult<
    AxiosResponse<any>,
    unknown,
    PostPredictDictionaryProps
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(postPredictDictionary, {
        onSuccess: () => {
            commonShowAlert(
                showAlert,
                hideAlert,
                'success',
                'Dictionary has been predicted successfully'
            );
        },
        onError: (error: any) => {
            const errorMessage: string =
                error?.data?.message ?? 'Unable to predict dictionary';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};
